<template>
	<div class="vue-record-audio" :class="{ active: isRecording, paused: isPaused }">
		<div v-show="!uploading" class="flex-space-between--center">
			<v-btn @click="currentState.callback()" class="left-side flex-1 flex-start--center">
				<div class="btn-behaviour flex-centerize">
					<component :is="currentState.icon" />
				</div>
				<div class="btn-behaviour flex-centerize">
					<div class="description-text ml-2">{{ currentState.text }}</div>
				</div>
			</v-btn>
			<v-btn v-if="hasRecording" @click="resetSelected()" text color="error"> Delete record </v-btn>
		</div>
		<div v-show="uploading" class="app-loader-container flex-centerize">
			<AppLoader size="15px" />
		</div>
	</div>
</template>

<script>
import api from '@/api';
import axios from 'axios';

import LoaderWrapper from '@/components/common/LoaderWrapper.vue';

import RecorderMixin from './RecorderMixin';

import RecordSvg from './icons/RecordSvg';
import PlayRecordingSvg from './icons/PlayRecordingSvg';
import StopRecordingSvg from './icons/StopRecordingSvg';

const supportedTypes = ['audio/aac', 'audio/ogg', 'audio/wav', 'audio/webm'];

const confObject = (icon, text, callback) => ({ icon, text, callback });

export default {
	name: 'VueRecordAudio',
	mixins: [RecorderMixin],
	components: {
		LoaderWrapper,
		RecordSvg,
		PlayRecordingSvg,
		StopRecordingSvg,
	},

	props: {
		selectedRecord: { type: [String, Object] },
		fieldValue: { type: String },
		widgetID: { type: [String, Number] },
		mimeType: { type: String, default: 'audio/webm', validator: (v) => supportedTypes.includes(v) },
	},
	computed: {
		urlFullPath() {
			if (!this.uploadParams) {
				return '';
			} else {
				const params = this.uploadParams;
				return `${params.url}${params.fields.key}`;
			}
		},
		hasRecording() {
			return !!this.selectedRecord;
		},
		currentState() {
			const { isRecording, hasRecording } = this;
			if (isRecording) {
				return confObject(StopRecordingSvg, 'Recording...', () => this.stop());
			} else if (!isRecording && !hasRecording) {
				return confObject(RecordSvg, 'Press to start recording', () => this.start());
			}
			return confObject(PlayRecordingSvg, 'Press to play recording', () => this.playSound(this.selectedRecord));
		},
	},
	methods: {
		resetSelected() {
			this.$emit('update:selectedRecord', null);
		},
		playSound(sound) {
			if (sound) {
				var audio = new Audio(sound);
				audio.play();
			}
		},
		async getSignedUrl() {
			const r = await api.getWidgetFieldUploadUrl(this.widgetID, 'record.mp3');
			this.uploadParams = r;
			return this.uploadParams;
		},
		async upload() {
			if (!this.uploadParams) {
				await this.getSignedUrl();
			}
			const postData = new FormData();
			for (const key in this.uploadParams.s3.fields) {
				postData.append(key, this.uploadParams.s3.fields[key]);
			}
			const blob = await fetch(this.selectedRecord).then((r) => r.blob());
			postData.append('file', blob, 'record.mp3');
			const r = await axios.post(this.uploadParams.s3.url, postData, { 'x-amz-acl': 'public-read' });
			this.$emit('update:fieldValue', this.uploadParams.file_url);
			return r;
		},
	},
	watch: {
		async selectedRecord(newV) {
			if (newV) {
				this.uploading = true;
				await this.upload();
				this.uploading = false;
			} else {
				this.$emit('update:fieldValue', newV);
			}
		},
	},
	data() {
		return {
			uploading: false,
			uploadParams: null,
		};
	},
};
</script>

<style scoped lang="scss">
.vue-record-audio {
	$height: 36px;

	min-height: $height;
	border: var(--widget-field-border);
	border-radius: var(--widget-text-field-border-radius);

	.app-loader-container {
		height: $height;
	}

	.description-text {
		color: #a1a1a1;
		font-size: 15px;
	}

	.left-side {
		.btn-behaviour {
			> div {
				display: flex;
				align-items: center;
				justify-content: center;
			}
		}
	}

	::v-deep(.v-spinner) {
		height: 18px;
	}

	::v-deep(button.v-btn) {
		background-color: transparent;
		border-radius: 0 !important;
	}

	@media (max-width: $device-sm) {
		background-color: var(--widget-field-mobile-bg);
	}
}
</style>
